import RelativePositionComponent from '../../../Component/Render/RelativePosition';
import { StreakBarLayout } from '../../../Layouts/StreakBar/StreakBarLayout';
import SpriteComponent from '../../../Component/Render/Sprite';
import { STREAK_PROGRESS_STEPS } from '../../../Constants/Level';
import Renderable from '../../../Component/Renderable';
import Game from '../../../Game';
import { UIActions } from '../../../State/UI/UIActions';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import { Anim, StartAnimationClipAsync } from '../../../System/Animation/AnimationHelper';
import TransformComponent from '../../../Component/Transform';
import { PopupID } from '../../../State/UI/PopupID';
import { StreakReward } from '../../../State/Card/ICardState';
import { CardColor } from '../../../Utility/CardsHelper';
import { Deferred } from '../../../Utility/Deferred';
import { Easing } from '@tweenjs/tween.js';
import Armature from '../../Armature';
import { DragonbonesDefault } from '../../../System/Animation/Clip';
import { sleep } from '../../../Utility/async';
import { Layout } from '../../../Utility/layout/createLayout';

const rewardAssetByStreakReward: Record<StreakReward, string> = {
    [StreakReward.OneCard]: 'combo_reward_card_1',
    [StreakReward.TwoCards]: 'combo_reward_card_2',
    [StreakReward.Joker]: 'combo_reward_joker_1',
};

const pipAssetByCardColor: Record<CardColor, string> = {
    [CardColor.Red]: 'combo_red',
    [CardColor.Black]: 'combo_black',
};

type Subscriber = { turns: number; deferred: Deferred };

export default class StreakBar {
    public static instance: StreakBar | null = null;
    private readonly layout: any;
    private prevMatchSequenceLength: number = 0;
    private readonly goldCircle: Armature;
    private subscribers: Subscriber[] = [];
    private lastUpdateTurns: number = -1;

    constructor() {
        StreakBar.instance = this;
        this.layout = new StreakBarLayout();
        this.layout.progressStroke.getComponent(Renderable).alpha = 0;
        this.layout.bg.on(GameObjectEvent.SELECTED, this.handleBgClick);
        this.layout.glow.getComponent(Renderable).visible = false;

        this.layout.root.on(GameObjectEvent.DESTROYED, () => {
            StreakBar.instance = null;
        });

        this.layout.pips.forEach((pip:any) => {
            pip.getComponent(Renderable).alpha = 0;
        });

        this.goldCircle = GameObject.Instantiate(Armature, {
            dragonbonesName: 'combo_meter_gold_state',
            armatureName: 'Armature',
            playOnStart: false,
        });
        this.layout.goldAnimationContainer.getComponent(Renderable).alpha = 0;
        this.layout.goldAnimationContainer.addChild(this.goldCircle);
    }

    public get root(): GameObject {
        return this.layout.root;
    }

    public get reward(): GameObject {
        return this.layout.reward;
    }

    public get glow(): GameObject {
        return this.layout.glow;
    }

    public async updatePromise(turns: number): Promise<void> {
        if (turns <= this.lastUpdateTurns) return;

        const subscriber: Subscriber = { turns, deferred: new Deferred() };
        this.subscribers.push(subscriber);

        return subscriber.deferred.read.promise;
    }

    public update(props?: { isUndo?: boolean }): void {
        const cardsState = Game.store.getState().levelState.cardsState;
        const matchSequence = cardsState.matchSequence;
        const matchSequenceLength = matchSequence.length % STREAK_PROGRESS_STEPS;
        const isStreak = matchSequence.length > 0 && matchSequenceLength === 0 && !props?.isUndo;
        const roundedLen = Math.floor(matchSequence.length / STREAK_PROGRESS_STEPS) * STREAK_PROGRESS_STEPS;
        const colors = matchSequence.slice(isStreak ? -STREAK_PROGRESS_STEPS : roundedLen);

        colors.forEach((color, i) => {
            this.layout.pips[i].getComponent(SpriteComponent).texture = pipAssetByCardColor[color];
        });

        if (!isStreak) {
            this.updateIconTexture();
        }

        if (colors.length > this.prevMatchSequenceLength) {
            this.playLightUpAnimation(colors.length, this.prevMatchSequenceLength);

            if (isStreak) {
                this.playFullFillAnimation();
            }
        } else if (colors.length < this.prevMatchSequenceLength) {
            this.playResetAnimation(colors.length, this.prevMatchSequenceLength, props?.isUndo);
        }

        this.subscribers = this.subscribers.filter((subscriber) => {
            if (subscriber.turns <= cardsState.turns) {
                subscriber.deferred.control.resolve();
                return false;
            }

            return true;
        });

        this.prevMatchSequenceLength = matchSequenceLength;
        this.lastUpdateTurns = cardsState.turns;
    }

    public async playRewardAnimation(): Promise<void> {
        Anim(this.layout.reward, { scaleX: 0, scaleY: 0, alpha: 0, duration: 400, easing: Easing.Sinusoidal.In });

        await sleep(500);

        const promises = this.layout.pips.map(async (pip:any, i:number) => {
            if (i < this.prevMatchSequenceLength) return;

            const delay = (this.layout.pips.length - 1 - i) * 50;
            await Anim(pip, { alpha: 0, duration: 300, delay });
        });

        await Promise.all(promises);

        this.updateIconTexture();

        Anim(this.layout.reward, { scaleX: 1, scaleY: 1, alpha: 1, duration: 500, easing: Easing.Back.Out });
        Anim(this.layout.progressStroke, { alpha: 0, duration: 500 });
        Anim(this.layout.goldAnimationContainer, { alpha: 0, duration: 500 });
    }

    private async playFullFillAnimation(): Promise<void> {
        Anim(this.layout.progressStroke, { alpha: 1, duration: 200 });
        Anim(this.layout.goldAnimationContainer, { alpha: 1, duration: 200 });

        this.goldCircle.getComponent(Renderable).visible = true;

        StartAnimationClipAsync(new DragonbonesDefault(this.goldCircle, {
            animationName: 'animtion0',
            playTimes: 1,
        }));
    }

    private async playLightUpAnimation(length: number, prevLength: number): Promise<void> {
        for (let i = prevLength; i < length; i++) {
            const pip = this.layout.pips[i];
            const delay = (i - prevLength) * 100;
            const easing = Easing.Quadratic.In;
            pip.getComponent(TransformComponent).scale.set(2.34);

            Anim(pip, { alpha: 1, duration: 50, delay });
            Anim(pip, { scaleX: 1, scaleY: 1, alpha: 1, duration: 250, easing, delay });
        }
    }

    private async playResetAnimation(length: number, prevLength: number, isUndo: boolean): Promise<void> {
        if (!isUndo) {
            this.layout.reward.getComponent(TransformComponent).angle = 0;
            Anim(this.layout.reward, { angle: [-15, 15, -15, 15, 0], duration: 500 });
        }

        for (let i = prevLength - 1, n = 0; i >= length; i--, n++) {
            const delay = n * 50;
            const easing = Easing.Quadratic.In;
            Anim(this.layout.pips[i], { scaleX: 0.55, scaleY: 0.55, alpha: 0, duration: 250, easing, delay });
        }
    }

    private updateIconTexture(): void {
        const cardsState = Game.store.getState().levelState.cardsState;
        this.layout.reward.getComponent(SpriteComponent).texture = rewardAssetByStreakReward[cardsState.streakReward];
    }

    private readonly handleBgClick = (): void => {
        UIActions.showPopup(PopupID.STREAK_INFO_POPUP, {});
    };
}
